import { ENDPOINT } from '~/constants';

import { isAbortError, type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { i18n, log } from '~/utils';

import {
  type SupplierRequestListResponse,
  type SupplierRequestsQueryParams,
} from './types';
import {
  zodSupplierRequestListResponseSchema,
  zodSupplierRequestsQueryParamsSchema,
} from './validations';

/**
 * Fetches the paginated list of supplier requests from the admin API.
 *
 * @param searchParams - Query parameters (state filter, requesting company account filter, pagination). Must be pre-validated.
 * @param options - Optional request options including abort signal.
 * @returns Paginated list of supplier requests.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Requests/operation/list_requests_vadmin_supplier_requests__get
 */
export const fetchSupplierRequests = async (
  searchParams: SupplierRequestsQueryParams,
  options?: KyRequestOptions,
) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.SUPPLIER_REQUEST.GET_ALL(), {
        searchParams,
        ...options,
        validate: {
          ...options?.validate,
          response: zodSupplierRequestListResponseSchema,
          searchParams: zodSupplierRequestsQueryParamsSchema.transform(
            (value) =>
              withoutObjectKeysWhereValueIs(value, [null, undefined, '']),
          ),
        },
      })
      .json<SupplierRequestListResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching supplier requests',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.supplierRequest.getAll.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.supplierRequest.getAll.error.generic, {
            ns: 'data',
          });
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
